import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faFileAlt, 
  faImage, 
  faSpinner, 
  faExclamationCircle, 
  faCheckCircle, 
  faExclamationTriangle,
  faArrowUpRightFromSquare,
  faFile,
  faFileWord,
  faFilePdf,
  faFileExcel,
  faFilePowerpoint,
  faFileImage
} from '@fortawesome/free-solid-svg-icons';

const HUGGINGFACE_API_TOKEN = "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";
const MODEL_ID = "sentence-transformers/all-MiniLM-L6-v2";
const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

function SemanticSearch() {
  // State management
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [useOCR, setUseOCR] = useState(false);
  const searchRef = useRef(null);

  // Get file icon based on extension
  const getFileIcon = (path) => {
    if (!path) return faFile;
    const extension = path.split('.').pop().toLowerCase();
    switch(extension) {
      case 'pdf': return faFilePdf;
      case 'doc':
      case 'docx': return faFileWord;
      case 'xls':
      case 'xlsx': return faFileExcel;
      case 'ppt':
      case 'pptx': return faFilePowerpoint;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return faFileImage;
      default: return faFile;
    }
  };

  // Fetch documents on mount and whenever useOCR changes
  useEffect(() => {
    const fetchDocuments = async () => {
      setDocumentsLoading(true);
      try {
        const endpoint = useOCR ? "/documents/ocr" : "/documents";
        const response = await axios.get(`${API_BASE_URL}${endpoint}`);
        if (response.data?.length) {
          const docs = response.data.map(doc => ({
            id: doc.id || doc.path || doc.title,
            title: doc.title || "Untitled Document",
            text: doc.text || doc.content || "",
            path: doc.path,
            metadata: doc.metadata || {},
            author: doc.author || "",
            keywords: doc.keywords || [],
            description: doc.description || "",
            lastModified: doc.lastModified || null
          }));
          setDocuments(docs);
        } else {
          setDocuments([]);
        }
      } catch (err) {
        setError(`Failed to load documents: ${err.message}`);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocuments();
  }, [useOCR]);

  // Generate recommendations based on query while typing
  useEffect(() => {
    if (isTyping && query.length > 0 && !loading) {
      const timer = setTimeout(() => {
        const matched = documents.flatMap(doc => {
          const matches = [];
          
          if (doc.title.toLowerCase().includes(query.toLowerCase())) {
            matches.push({
              type: 'title',
              text: doc.title,
              path: doc.path,
              docId: doc.id
            });
          }
          
          const textPreview = doc.text.substring(0, 100);
          if (textPreview.toLowerCase().includes(query.toLowerCase())) {
            matches.push({
              type: 'text',
              text: textPreview,
              path: doc.path,
              docId: doc.id
            });
          }
          
          return matches;
        });
        
        setRecommendations(matched.slice(0, 5));
        setShowRecommendations(matched.length > 0);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowRecommendations(false);
    }
  }, [query, documents, loading, isTyping]);

  // Handle document search
  const handleSearch = useCallback(async (searchQuery = query) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsTyping(false);
    setLoading(true);
    setError(null);
    setShowRecommendations(false);

    try {
      const documentTexts = documents.map(doc => {
        return `Title: ${doc.title}\nContent: ${doc.text}\nPath: ${doc.path}`.trim();
      });

      const { data } = await axios.post(
        `https://router.huggingface.co/hf-inference/models/sentence-transformers/all-MiniLM-L6-v2/pipeline/sentence-similarity`,
        {
          inputs: {
            source_sentence: searchQuery,
            sentences: documentTexts
          }
        },
        {
          headers: {
            Authorization: `Bearer ${HUGGINGFACE_API_TOKEN}`
          },
          timeout: 30000
        }
      );

      if (Array.isArray(data)) {
        setResults(documents
          .map((doc, i) => ({ ...doc, score: data[i] || 0 }))
          .filter(d => d.score > 0.1)
          .sort((a, b) => b.score - a.score)
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [query, documents]);

  // Handle typing state
  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setIsTyping(true);
  };

  // Handle opening document directly
  const openDocument = (path) => {
    // Replace 'localhost' with the IP address
    const updatedPath = path.replace('localhost', '10.1.16.19');
    window.open(updatedPath, '_blank');
  };
  

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get match quality color
  const getMatchColor = (score) => {
    if (score > 0.7) return 'text-success';
    if (score > 0.4) return 'text-warning';
    return 'text-secondary';
  };

  return (
    <div className="container-fluid py-4" ref={searchRef}>
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <div className="text-center mb-4">
                <h1 className="h3 fw-bold text-primary mb-2">
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  Document Search
                </h1>
                <p className="text-muted">Find documents using semantic search technology</p>
              </div>

              {/* Search mode toggle */}
              <div className="d-flex justify-content-center gap-2 mb-4">
                <button
                  className={`btn ${useOCR ? 'btn-outline-primary' : 'btn-primary'} rounded-pill px-4`}
                  onClick={() => setUseOCR(false)}
                  disabled={loading || documentsLoading}
                >
                  <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                  Regular Search
                </button>
                <button
                  className={`btn ${useOCR ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-4`}
                  onClick={() => setUseOCR(true)}
                  disabled={loading || documentsLoading}
                >
                  <FontAwesomeIcon icon={faImage} className="me-2" />
                  OCR Search
                </button>
              </div>

              {/* Search input */}
              <div className="position-relative mb-4">
                <div className="input-group input-group-lg">
                  <span className="input-group-text bg-white border-end-0">
                    <FontAwesomeIcon icon={faSearch} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 ps-0"
                    placeholder="Search documents..."
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setIsTyping(true)}
                    onBlur={() => setTimeout(() => setIsTyping(false), 200)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    disabled={loading || documentsLoading}
                  />
                  <button
                    className="btn btn-primary px-4"
                    onClick={() => handleSearch()}
                    disabled={loading || documentsLoading || !query.trim()}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                        Searching...
                      </>
                    ) : (
                      'Search'
                    )}
                  </button>
                </div>

                {/* Recommendations dropdown */}
                {isTyping && showRecommendations && (
                  <div className="position-absolute top-100 start-0 end-0 z-3 mt-1 shadow-lg">
                    <div className="card border-0">
                      <div className="card-header bg-light py-2">
                        <small className="text-muted">Quick matches</small>
                      </div>
                      <div className="card-body p-0">
                        {recommendations.length > 0 ? (
                          <div className="list-group list-group-flush">
                            {recommendations.map((rec, i) => (
                              <button
                                key={i}
                                className="list-group-item list-group-item-action border-0 py-2"
                                onClick={() => {
                                  const doc = documents.find(d => d.id === rec.docId);
                                  if (doc) {
                                    setQuery(doc.title);
                                    handleSearch(doc.title);
                                  }
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    <FontAwesomeIcon 
                                      icon={rec.type === 'title' ? faFileAlt : faFile} 
                                      className={`text-${rec.type === 'title' ? 'primary' : 'muted'}`} 
                                    />
                                  </div>
                                  <div className="flex-grow-1 text-start">
                                    <div className="d-flex justify-content-between">
                                      <h6 className="mb-1">
                                        {rec.type === 'title' ? rec.text : 'Content match'}
                                      </h6>
                                      <small className="text-primary ms-2">Open</small>
                                    </div>
                                    {rec.type === 'text' && (
                                      <p className="small text-muted mb-0">{rec.text}...</p>
                                    )}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="p-3 text-center text-muted">
                            <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                            No matches found
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Status indicators */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  {documentsLoading && (
                    <span className="badge bg-info bg-opacity-10 text-info">
                      <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                      Loading documents...
                    </span>
                  )}
                  {!documentsLoading && documents.length > 0 && (
                    <span className="badge bg-success bg-opacity-10 text-success">
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                      {documents.length} documents loaded
                    </span>
                  )}
                  {error && (
                    <span className="badge bg-danger bg-opacity-10 text-danger">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                      Error loading documents
                    </span>
                  )}
                </div>
                {results.length > 0 && (
                  <div className="text-muted small">
                    Showing {results.length} of {documents.length} documents
                  </div>
                )}
              </div>

              {/* Results table */}
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th width="45%">Document</th>
                      <th width="15%">Type</th>
                      <th width="20%">Match</th>
                      <th width="20%">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-5">
                          <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary" />
                          <p className="mt-3">Searching documents...</p>
                        </td>
                      </tr>
                    ) : results.length > 0 ? (
                      results.map((result) => (
                        <tr key={result.id} className="cursor-pointer">
                          <td onClick={() => openDocument(result.path)}>
                            <div className="d-flex align-items-center">
                              <div className="me-3">
                                <FontAwesomeIcon 
                                  icon={getFileIcon(result.path)} 
                                  size="lg" 
                                  className="text-primary" 
                                />
                              </div>
                              <div>
                                <div className="fw-bold">{result.title}</div>
                                {result.description && (
                                  <div className="text-muted small">{result.description.substring(0, 60)}...</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td onClick={() => openDocument(result.path)}>
                            <span className="badge bg-light text-dark">
                              {result.path?.split('.').pop() || 'file'}
                            </span>
                          </td>
                          <td onClick={() => openDocument(result.path)}>
                            <div className="d-flex align-items-center">
                              <div className="progress flex-grow-1" style={{height: '8px'}}>
                                <div 
                                  className={`progress-bar bg-${result.score > 0.7 ? 'success' : result.score > 0.4 ? 'warning' : 'secondary'}`}
                                  role="progressbar" 
                                  style={{width: `${Math.round(result.score * 100)}%`}}
                                  aria-valuenow={Math.round(result.score * 100)}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                ></div>
                              </div>
                              <div className={`ms-2 ${getMatchColor(result.score)}`}>
                                {Math.round(result.score * 100)}%
                              </div>
                            </div>
                          </td>
                          <td>
                            <button 
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openDocument(result.path)}
                            >
                              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="me-1" />
                              Open
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : query && !loading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-5">
                          <FontAwesomeIcon icon={faExclamationCircle} size="2x" className="text-muted" />
                          <h5 className="mt-3 mb-2">No results found</h5>
                          <p className="text-muted">Try different keywords or check your search query</p>
                          <button 
                            className="btn btn-outline-primary mt-2"
                            onClick={() => setQuery('')}
                          >
                            Clear search
                          </button>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              {/* Error display */}
              {error && (
                <div className="alert alert-danger mt-3 d-flex align-items-center" role="alert">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <div>{error}</div>
                </div>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="text-center mt-4 text-muted small">
            <p>Powered by HuggingFace Transformers and semantic search technology</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SemanticSearch;